import { DELIVERY_STATES, EVENTS } from "constants";
import { dispatchEvent } from "modules/eventsManager";
import * as gamesAPI from "modules/gamesAPI";
import { log } from "modules/logs";
import * as Communication from "./communication/communication";
import { STREAMS } from "./communication/communicationTypes";
import utils from "./utils";

let packagesStatusArrived = false
let packageDeliveryStatusArrived = false

Communication.init(async () => {
  log("native > web | waiting for packages & delivery statuses to arrive")

  packagesStatusSubscribe(packageStatusHandler)
  packageDeliveryStatusSubscribe(packageDeliveryStatusHandler)

  await utils.waitUntil(
    () =>
      packagesStatusArrived === true && packageDeliveryStatusArrived === true
  )
}).then(() => {
  log("Communication init completed")
})

function packageStatusHandler() {
  packagesStatusArrived = true
}

function packageDeliveryStatusHandler() {
  packageDeliveryStatusArrived = true
}

async function isDeliveryApproved(packageName) {
  const installPermissionStatus = await Communication.permissionsStatus("install")
  if (installPermissionStatus.status.type === "granted") {
    log("install permission already granted - moving on...")
    return true;
  }

  // Application dialog
  if (!(await showInstallPermissionsModal())) {
    log("install permission dialog dismissed")
    setPackageDeliveryStatus(packageName, null)
    return false;
  }
  log("install permission dialog approved")

  // native dialog / settings
  const installPermissionRes = await Communication.permissionRequest("install")
  if (installPermissionRes?.status?.type === "granted") {
    return true;
  }

  setPackageDeliveryStatus(packageName, null)

  return false;
}

// List of installed packages
export async function packagesStatusSubscribe(handler, packages = null) {
  Communication.streamSubscribe(STREAMS.PACKAGE_STATUS, EVENTS.PACKAGES_STATUS, {
    packages,
  }, handler)
  // Request: packageName(optional) \
  // Response: [packageName, status(installed - with versionCode, installing..)]
  // Updates: Full status payload update
}

export async function packageDeliveryStatusSubscribe(handler, packages = null) {
  Communication.streamSubscribe(STREAMS.PACAKGE_DELIVERY_STATUS, EVENTS.DELIVERIES_STATUS,
    { packages }
    , handler)
  // Request: packageName(optional) \
  // Response: [{packageName, status, versionCode}]
  // Updates: [Full devlivery status]
}

export async function packageDeliver(appId, packageName, isUpdate = false) {
  // Request: token, delivery methods params (allowed on mobile data), report properties map \
  // Response: -
  setPackageDeliveryStatus(packageName, DELIVERY_STATES.sentToNative)

  if (Communication.isEnabled()) {
    const canDeliver = await isDeliveryApproved(packageName);

    if (canDeliver === false) {
      log(`delivery not approved`)
      return;
    }

    const result = await gamesAPI.delivery(appId);
    if (result.code && result.code !== 200) {
      // TODO: show error to user
      log(`error when trying to deliver. ${JSON.stringify(result)}`);
      setPackageDeliveryStatus(packageName, null)
      return;
    }

    const stringifiedToken = JSON.stringify(result);
    log(`delivery token: ${stringifiedToken}`);
    const base64EncodedToken = btoa(stringifiedToken);
    log(`delivery base64Token: ${base64EncodedToken}`);

    try {
      const action = isUpdate ? "update" : "install";

      const deliveryResponse = await Communication.send(
        "packages/deliveries/deliver",
        `callback:${action}|${packageName}`,
        {
          token: base64EncodedToken,
          deliveryProps: {
            // installingMessage: "Fucking {0}", // (optional)
            // installSuccessMessage: "Fuck", // (optional)
            // allowedOverMobileData: true, // (optional, default=false)
            // allowedOverRoaming: true, // (optional, default=true)
          },
          reportProps: {
            age: "20",
            gender: "female",
            snir: "bad"
          }
        }
      )
      return deliveryResponse;
    } catch (error) {
      const apkSize = result.delivery_methods[0]?.properties.size;
      log(`delivery error: ${JSON.stringify(error)}`)
      if (error.code === 201) {
        setPackageDeliveryStatus(packageName, DELIVERY_STATES.failed);

        dispatchEvent(EVENTS.MODAL_OPEN, {
          modalName: "InsufficientStorage",
          params: {
            size: apkSize
          },
        })
      } else {
        setPackageDeliveryStatus(packageName, DELIVERY_STATES.failed);

        dispatchEvent(EVENTS.MODAL_OPEN, {
          modalName: "UnknownError",
          params: {
            packageName,
            error
          },
        })
      }
    }
  } else {
    log("install permission not granted - requesting")
    if (!(await showInstallPermissionsModal())) {
      setPackageDeliveryStatus(packageName, null)
      return false;
    }

    // Mock delivery events
    mockDownloadCancelled = false
    const steps = [
      { type: DELIVERY_STATES.queued, wait: 1500 },
      {
        type: DELIVERY_STATES.downloading,
        progress: { totalSizeInBytes: 100, downloadedSizeInBytes: 0 },
        wait: 500,
      },
      {
        type: DELIVERY_STATES.downloading,
        progress: { totalSizeInBytes: 100, downloadedSizeInBytes: 33 },
        wait: 500,
      },
      {
        type: DELIVERY_STATES.downloading,
        progress: { totalSizeInBytes: 100, downloadedSizeInBytes: 66 },
        wait: 500,
      },
      {
        type: DELIVERY_STATES.downloading,
        progress: { totalSizeInBytes: 100, downloadedSizeInBytes: 100 },
        wait: 500,
      },
      { type: DELIVERY_STATES.installing, wait: 1500 },
      { type: DELIVERY_STATES.installed },
    ]

    for (let step of steps) {
      if (mockDownloadCancelled) break
      setPackageDeliveryStatus(packageName, step.type, step.progress)

      if (step.wait) await utils.sleep(step.wait)
    }
  }
}

function setPackageDeliveryStatus(packageName, type = null, progress = null) {
  dispatchEvent(
    EVENTS.DELIVERIES_STATUS, {
    deliveries: [
      {
        packageName,
        status: { type, progress },
      },
    ],
  })
}

function showInstallPermissionsModal() {
  return new Promise((resolve) => {
    function modalResponse(res) {
      dispatchEvent(EVENTS.MODAL_CLOSE)
      resolve(res)
    }

    dispatchEvent(EVENTS.MODAL_OPEN, {
      modalName: "InstallPermissions",
      params: {
        onConfirm: () => modalResponse(true),
        onReject: () => modalResponse(false)
      },
    })
  })
}

export async function testDeliveryReport(packageName) {
  const mockTokens = [
    // Harry Potter
    `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`,
  ]

  const token = btoa(
    JSON.stringify({
      ...JSON.parse(atob(mockTokens[0])),
      title: "niceTitle",
      icon: "iconPath",
    })
  )

  return Communication.send(
    "packages/deliveries/deliver",
    `callback:install|${packageName}`,
    {
      // Harry Potter
      token,

      //
      deliveryProps: {
        // installingMessage: "Fucking {0}", // (optional)
        // installSuccessMessage: "Fuck", // (optional)
        // allowedOverMobileData: true, // (optional, default=false)
        // allowedOverRoaming: true, // (optional, default=true)
      },
      reportProps: {
        age: "20",
        gender: "female",
        snir: "bad"
      }
    }
  )
}

var mockDownloadCancelled
export async function packageCancelDelivery(packageName) {
  log("packageCancelDelivery", packageName)

  if (Communication.isEnabled()) {
    return Communication.send(
      "packages/deliveries/cancel",
      `callback:cancel|${packageName}`,
      {
        packageName,
      }
    )
  } else {
    mockDownloadCancelled = true

    await utils.sleep(500)
    dispatchEvent(EVENTS.PACKAGES_STATUS, {
      packages: [
        {
          packageName,
          status: { type: DELIVERY_STATES.cancelled },
        },
      ],
    })
  }
}

export async function packageLaunch(packageName) {
  setPackageDeliveryStatus(packageName, DELIVERY_STATES.launched)

  return Communication.send("packages/launch", `launch:${packageName}`, { packageName })
}

export async function openUrl(url, type) {
  // Request: url, type (webview, tab, external) \
  if (Communication.isEnabled()) {
    return Communication.send("nav/openUrl", `callback:openURL${url}`, {
      url
    })
  } else {
    window.open(url, "_blank");
  }
}